import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import notificationsService from '../../services/notifications.service';
import { errorMessage } from '../errorMessage';
import { login, logout } from '../auth/authSlice';

// Pass the page number held in `next` to append that page; omit it to refresh.
export const fetchNotifications = createAsyncThunk(
  'notifications/fetch',
  async (page, { rejectWithValue }) => {
    try {
      return await notificationsService.list(page);
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const markRead = createAsyncThunk(
  'notifications/markRead',
  async (id, { rejectWithValue }) => {
    try {
      return await notificationsService.markRead(id);
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

export const markAllRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      return await notificationsService.markAllRead();
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  },
);

// DRF's `next` is an absolute URL built from the request, so behind a proxy it can
// carry the wrong scheme or host. Only its page number is trusted; anything
// unparseable means there are no more pages.
const nextPage = (next) => {
  if (!next) return null;
  try {
    const page = new URL(next).searchParams.get('page');
    return /^[1-9]\d*$/.test(page) ? page : null;
  } catch (e) {
    return null;
  }
};

const initialState = {
  results: [],
  unreadCount: 0,
  next: null,
  loading: false,
  error: null,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  // No reducers of its own: a change of user is handled unconditionally by the
  // login and logout cases below, so nothing needs to be dispatched by hand.
  reducers: {},
  extraReducers: (builder) => {
    builder
      // The list survives `pending` and `rejected`: the header refetches on every
      // route change, and a failed refetch must not empty what is being read.
      .addCase(fetchNotifications.pending, (state) => ({ ...state, loading: true }))
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const { results, unread_count: unreadCount, next } = action.payload;
        const seen = new Set(state.results.map((n) => n.id));
        const appended = action.meta.arg
          ? [...state.results, ...results.filter((n) => !seen.has(n.id))]
          : results;
        return { ...state, results: appended, unreadCount, next: nextPage(next), loading: false, error: null };
      })
      .addCase(fetchNotifications.rejected, (state, action) => ({
        ...state,
        loading: false,
        error: action.payload || 'Could not load notifications.',
      }))
      .addCase(markRead.fulfilled, (state, action) => {
        const id = action.meta.arg;
        const held = state.results.find((n) => n.id === id);
        // The server answers 200 whether or not the row was already read, so only
        // the row held here can say whether this call changed anything.
        const wasUnread = Boolean(held) && held.read_at === null;
        return {
          ...state,
          results: state.results.map((n) => (n.id === id ? { ...n, ...action.payload } : n)),
          unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
        };
      })
      .addCase(markRead.rejected, (state, action) => ({
        ...state, error: action.payload || 'Could not mark the notification read.',
      }))
      .addCase(markAllRead.fulfilled, (state) => {
        const now = new Date().toISOString();
        return {
          ...state,
          results: state.results.map((n) => (n.read_at === null ? { ...n, read_at: now } : n)),
          unreadCount: 0,
        };
      })
      .addCase(markAllRead.rejected, (state, action) => ({
        ...state, error: action.payload || 'Could not mark notifications read.',
      }))
      // A fetch issued for the previous user can land after logout; resetting on
      // login too means the next person always starts clean.
      .addCase(logout.fulfilled, () => initialState)
      .addCase(login.fulfilled, () => initialState);
  },
});

export const selectNotifications = (state) => state.notifications.results;
export const selectUnreadCount = (state) => state.notifications.unreadCount;
export const selectNotificationsNext = (state) => state.notifications.next;
export const selectNotificationsLoading = (state) => state.notifications.loading;
export const selectNotificationsError = (state) => state.notifications.error;

export default notificationsSlice.reducer;
